import { Alert, Button, Checkbox, FormHelperText, Grid, Link, Typography } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { t } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { toast } from 'react-toastify';
import { useState } from 'react';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { activateKivra } from 'src/shared/services/invoiceGroupService';
import { ActivateKivraRequest } from '../models/activateKivraRequest';
import { KivraImageUpload } from './kivraImageUpload';

type KivraConnectModalProps = {
  kivraConnectModal: boolean;
  setKivraConnectModal: (kivraModal: boolean) => void;
  invoiceGroupId: number;
  refetchInvoiceGroup: () => void;
};

export const KivraConnectModal = (props: KivraConnectModalProps) => {
  const { kivraConnectModal, setKivraConnectModal, invoiceGroupId, refetchInvoiceGroup } = props;

  const isMounted = useIsMounted();

  const [checked, setChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const kivraConnectModalSchema: Schema<ActivateKivraRequest> = yup.object().shape({
    companyName: yup.string().required(t('common:validationMessages.required')),
    legalCompanyName: yup.string().required(t('common:validationMessages.required')),
    orgNr: yup.string().required(t('common:validationMessages.required')),
    iconName: yup.string().required(t('common:validationMessages.required')),
    iconBase64: yup.string().required(t('common:validationMessages.required')),
  });

  const kivraFormMethods = useForm<ActivateKivraRequest>({
    resolver: yupResolver(kivraConnectModalSchema),
    mode: 'all',
    defaultValues: {
      companyName: '',
      legalCompanyName: '',
      orgNr: '',
      iconName: '',
      iconBase64: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors },
  } = kivraFormMethods;

  const extractBase64Data = (s: string) => {
    if (!s) return '';
    const m = s.match(/^data:[^;]+;base64,(.*)$/);
    return m ? m[1] : s;
  };

  const onSubmit = async (data: ActivateKivraRequest) => {
    setLoading(true);

    const dataToPost: ActivateKivraRequest = {
      companyName: data.companyName,
      legalCompanyName: data.legalCompanyName,
      orgNr: data.orgNr,
      iconName: data.iconName,
      iconBase64: extractBase64Data(data.iconBase64),
    };

    try {
      const res = await activateKivra(invoiceGroupId, dataToPost);
      ConsoleHelper.log('Activate-Kivra RESPONS:', res);
      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupDetailPage.kivraConnectSuccess')}</>);
        refetchInvoiceGroup();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:adminInvoiceGroupDetailPage.kivraConnectError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <GenericDialog
      maxWidth='sm'
      disableBackdropClosing
      open={kivraConnectModal}
      dialogTitle={t('invoice:adminInvoiceGroupDetailPage.connectKivra')}
      dialogContent={
        <FormProvider {...kivraFormMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} p={0}>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12}>
                <Alert
                  icon={false}
                  severity='info'
                  sx={{
                    '& .MuiAlert-message': { width: '100% !important' },
                    fontWeight: 500,
                    m: -2,
                  }}
                >
                  {t('invoice:adminInvoiceGroupDetailPage.connectKivraDescription')}
                </Alert>
              </Grid>
              <Grid item xs={12} mt={2}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.companyName')} name='companyName' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.legalCompanyName')} name='legalCompanyName' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.orgNr')} name='orgNr' />
              </Grid>
              <Grid item xs={12} mb={2}>
                <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceGroupDetailPage.icon')}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceGroupDetailPage.iconRequirements')}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceGroupDetailPage.formatRequirement')}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{t('invoice:adminInvoiceGroupDetailPage.sizeRequirement')}</Typography>
                {errors.iconBase64?.message && (
                  <Grid item xs={12} ml={1}>
                    <FormHelperText error>{t('invoice:adminInvoiceGroupDetailPage.iconRequired')}</FormHelperText>
                  </Grid>
                )}
              </Grid>

              <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
                <Grid item xs={6} sx={{ display: 'flex', width: '100%', alignItems: 'stretch', '& > *': { flex: 1 } }}>
                  <KivraImageUpload />
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      }
      dialogActions={
        <Grid container justifyContent='space-between'>
          <Grid item>
            <Grid container alignItems='center' wrap='nowrap'>
              <Checkbox
                checked={checked}
                onChange={(_e, v) => setChecked(v)}
                inputProps={{ 'aria-label': t('invoice:adminInvoiceGroupDetailPage.acceptKivraTerms') }}
              />
              <Typography>
                {t('invoice:adminInvoiceGroupDetailPage.acceptKivra')}

                <Link
                  sx={{ ml: 0.5 }}
                  href='https://assets.ctfassets.net/7jy2i7elrvww/3qqXCwadDnr8Ww1kiWXkIN/febe1c2ac5557a5f9f2f6ef535d0426e/Allma_nna_villkor_fo_r_avsa_ndare.pdf'
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='hover'
                >
                  {t('invoice:adminInvoiceGroupDetailPage.terms')}
                </Link>
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Button
              autoFocus
              variant='contained'
              endIcon={<ArrowForwardIos fontSize='small' />}
              color='primary'
              disableElevation
              disabled={!checked}
              onClick={() => kivraFormMethods.handleSubmit(onSubmit)()}
            >
              {t('common:button.continue')}
            </Button>
          </Grid>
        </Grid>
      }
      onClose={() => setKivraConnectModal(false)}
      onSave={() => {
        ConsoleHelper.log();
      }}
    />
  );
};
